import { useState } from 'react'

import { AccordionSection } from '@/components/ui/AccordionSection'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { MatchScoreBadge } from '@/components/ui/MatchScoreBadge'
import { MultiSuggestInput } from '@/components/ui/MultiSuggestInput'
import { Pagination } from '@/components/ui/Pagination'
import { ProfileCard } from '@/components/ui/ProfileCard'
import { CandidateDetailDrawer } from '@/features/recruiter/components/CandidateDetailDrawer'
import { EducationLevelFilterFields } from '@/features/recruiter/components/EducationLevelFilterFields'
import { KeywordChipInput } from '@/features/recruiter/components/KeywordChipInput'
import { ReminderTaskModal } from '@/features/recruiter/components/ReminderTaskModal'
import { SkillExperienceInput } from '@/features/recruiter/components/SkillExperienceInput'
import type {
  CandidateSearchFilters,
  EducationLevelFilterInput,
  KeywordChip,
  KeywordMatchedCandidate,
  KeywordScope,
  NoticePeriodBucket,
  SkillChip,
} from '@/features/recruiter/api/candidates.api'
import {
  useCandidateFilterOptions,
  useCandidateSavedSearches,
  useCandidateSearchV2Query,
  useDeleteCandidateSavedSearch,
  useRecentCandidateSearches,
  useSaveCandidateSearch,
} from '@/features/recruiter/hooks/useCandidates'
import { formatRelativeTime } from '@/utils/format'

const SHOW_PROFILE_COMPLETION = false

const DEFAULT_PAGE_SIZE = 40
const PAGE_SIZE_OPTIONS = [20, 40, 100]

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'experience_desc', label: 'Experience (High to Low)' },
  { value: 'recent_update', label: 'Recently Updated' },
]

const NOTICE_PERIOD_OPTIONS: { value: NoticePeriodBucket | ''; label: string }[] = [
  { value: '', label: 'Any' },
  { value: 'immediate', label: 'Immediate' },
  { value: '0_15_days', label: '0-15 Days' },
  { value: '1_month', label: '1 Month' },
  { value: '2_months', label: '2 Months' },
  { value: '3_months', label: '3 Months' },
  { value: 'more_than_3_months', label: 'More than 3 Months' },
]

const EMPTY_EDUCATION_LEVEL: EducationLevelFilterInput = { mode: 'any' }

type TriState = '' | 'yes' | 'no'

function triStateToBool(v: TriState): boolean | undefined {
  return v === '' ? undefined : v === 'yes'
}

interface Draft {
  keywords: KeywordChip[]
  keywordScope: KeywordScope
  skillFilters: SkillChip[]
  locations: string[]
  willingToRelocate: TriState
  excludeAnywhere: boolean
  experience_min_years: string
  experience_max_years: string
  salaryCurrency: string
  salary_min: string
  salary_max: string
  currentSalaryMin: string
  currentSalaryMax: string
  includeUndisclosedSalary: boolean
  noticePeriodBucket: NoticePeriodBucket | ''
  isServingNoticePeriod: TriState
  currentCompany: string
  previousCompany: string
  excludeCompanies: string[]
  currentDesignation: string
  previousDesignation: string
  excludeDesignations: string[]
  industryIds: number[]
  departmentTaxonomyIds: number[]
  educationUg: EducationLevelFilterInput
  educationPg: EducationLevelFilterInput
  educationDoctorate: EducationLevelFilterInput
  gender: CandidateSearchFilters['gender'] | ''
  hasCareerBreak: TriState
  isDifferentlyAbled: TriState
  defenceBackground: CandidateSearchFilters['defence_background'] | ''
  candidateCategory: CandidateSearchFilters['candidate_category'] | ''
  ageMin: string
  ageMax: string
  workPermitCountries: string[]
  sort: CandidateSearchFilters['sort']
}

const EMPTY_DRAFT: Draft = {
  keywords: [],
  keywordScope: 'entire_resume',
  skillFilters: [],
  locations: [],
  willingToRelocate: '',
  excludeAnywhere: false,
  experience_min_years: '',
  experience_max_years: '',
  salaryCurrency: '',
  salary_min: '',
  salary_max: '',
  currentSalaryMin: '',
  currentSalaryMax: '',
  includeUndisclosedSalary: false,
  noticePeriodBucket: '',
  isServingNoticePeriod: '',
  currentCompany: '',
  previousCompany: '',
  excludeCompanies: [],
  currentDesignation: '',
  previousDesignation: '',
  excludeDesignations: [],
  industryIds: [],
  departmentTaxonomyIds: [],
  educationUg: EMPTY_EDUCATION_LEVEL,
  educationPg: EMPTY_EDUCATION_LEVEL,
  educationDoctorate: EMPTY_EDUCATION_LEVEL,
  gender: '',
  hasCareerBreak: '',
  isDifferentlyAbled: '',
  defenceBackground: '',
  candidateCategory: '',
  ageMin: '',
  ageMax: '',
  workPermitCountries: [],
  sort: 'relevance',
}

function draftToFilters(draft: Draft): CandidateSearchFilters {
  const filters: CandidateSearchFilters = {
    keywords: draft.keywords.length ? draft.keywords : undefined,
    keyword_scope: draft.keywordScope,
    skill_filters: draft.skillFilters.length ? draft.skillFilters : undefined,
    current_locations: draft.locations,
    willing_to_relocate: triStateToBool(draft.willingToRelocate),
    exclude_anywhere: draft.excludeAnywhere || undefined,
    experience_min_months: draft.experience_min_years ? Number(draft.experience_min_years) * 12 : undefined,
    experience_max_months: draft.experience_max_years ? Number(draft.experience_max_years) * 12 : undefined,
    salary_currency: draft.salaryCurrency || undefined,
    salary_min: draft.salary_min ? Number(draft.salary_min) : undefined,
    salary_max: draft.salary_max ? Number(draft.salary_max) : undefined,
    current_salary_min: draft.currentSalaryMin ? Number(draft.currentSalaryMin) : undefined,
    current_salary_max: draft.currentSalaryMax ? Number(draft.currentSalaryMax) : undefined,
    include_undisclosed_salary: draft.includeUndisclosedSalary || undefined,
    notice_period_bucket: draft.noticePeriodBucket || undefined,
    is_serving_notice_period: triStateToBool(draft.isServingNoticePeriod),
    current_company: draft.currentCompany || undefined,
    previous_company: draft.previousCompany || undefined,
    exclude_companies: draft.excludeCompanies.length ? draft.excludeCompanies : undefined,
    current_designation: draft.currentDesignation || undefined,
    previous_designation: draft.previousDesignation || undefined,
    exclude_designations: draft.excludeDesignations.length ? draft.excludeDesignations : undefined,
    industry_ids: draft.industryIds.length ? draft.industryIds : undefined,
    department_taxonomy_ids: draft.departmentTaxonomyIds.length ? draft.departmentTaxonomyIds : undefined,
    education_ug: draft.educationUg.mode !== 'any' ? draft.educationUg : undefined,
    education_pg: draft.educationPg.mode !== 'any' ? draft.educationPg : undefined,
    education_doctorate: draft.educationDoctorate.mode !== 'any' ? draft.educationDoctorate : undefined,
    gender: draft.gender || undefined,
    has_career_break: triStateToBool(draft.hasCareerBreak),
    is_differently_abled: triStateToBool(draft.isDifferentlyAbled),
    defence_background: draft.defenceBackground || undefined,
    candidate_category: draft.candidateCategory || undefined,
    age_min: draft.ageMin ? Number(draft.ageMin) : undefined,
    age_max: draft.ageMax ? Number(draft.ageMax) : undefined,
    work_permit_countries: draft.workPermitCountries.length ? draft.workPermitCountries : undefined,
    sort: draft.sort,
    page: 1,
  }

  return filters
}

// RecentCandidateSearch.filters / CandidateSavedSearch.filters come back
// as whatever was actually sent as query params (this page's own
// draftToFilters() shape) — this maps a logged/saved search back into a
// fillable draft. Falls back to sensible defaults for anything missing,
// since older recent-search rows predate the v2 filter fields.
function filtersToDraft(filters: Record<string, unknown>): Draft {
  const asStringArray = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  const asNumberArray = (v: unknown): number[] =>
    Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : []
  const asNumber = (v: unknown): number | null => (typeof v === 'number' ? v : null)
  const asBool = (v: unknown): TriState => (typeof v === 'boolean' ? (v ? 'yes' : 'no') : '')
  const asString = (v: unknown): string => (typeof v === 'string' ? v : '')
  const asEducationLevel = (v: unknown): EducationLevelFilterInput =>
    v && typeof v === 'object' ? { ...EMPTY_EDUCATION_LEVEL, ...(v as EducationLevelFilterInput) } : EMPTY_EDUCATION_LEVEL
  const asKeywords = (v: unknown): KeywordChip[] =>
    Array.isArray(v)
      ? v.filter((x): x is KeywordChip => !!x && typeof x === 'object' && typeof (x as KeywordChip).value === 'string')
      : []
  const asSkillFilters = (v: unknown): SkillChip[] =>
    Array.isArray(v)
      ? v.filter((x): x is SkillChip => !!x && typeof x === 'object' && typeof (x as SkillChip).name === 'string')
      : []

  const minMonths = asNumber(filters.experience_min_months)
  const maxMonths = asNumber(filters.experience_max_months)

  return {
    keywords: asKeywords(filters.keywords),
    keywordScope: (filters.keyword_scope as KeywordScope) || 'entire_resume',
    skillFilters: asSkillFilters(filters.skill_filters),
    locations: asStringArray(filters.current_locations),
    willingToRelocate: asBool(filters.willing_to_relocate),
    excludeAnywhere: filters.exclude_anywhere === true,
    experience_min_years: minMonths != null ? String(Math.round(minMonths / 12)) : '',
    experience_max_years: maxMonths != null ? String(Math.round(maxMonths / 12)) : '',
    salaryCurrency: asString(filters.salary_currency),
    salary_min: asNumber(filters.salary_min) != null ? String(filters.salary_min) : '',
    salary_max: asNumber(filters.salary_max) != null ? String(filters.salary_max) : '',
    currentSalaryMin: asNumber(filters.current_salary_min) != null ? String(filters.current_salary_min) : '',
    currentSalaryMax: asNumber(filters.current_salary_max) != null ? String(filters.current_salary_max) : '',
    includeUndisclosedSalary: filters.include_undisclosed_salary === true,
    noticePeriodBucket: (filters.notice_period_bucket as NoticePeriodBucket) || '',
    isServingNoticePeriod: asBool(filters.is_serving_notice_period),
    currentCompany: asString(filters.current_company),
    previousCompany: asString(filters.previous_company),
    excludeCompanies: asStringArray(filters.exclude_companies),
    currentDesignation: asString(filters.current_designation),
    previousDesignation: asString(filters.previous_designation),
    excludeDesignations: asStringArray(filters.exclude_designations),
    industryIds: asNumberArray(filters.industry_ids),
    departmentTaxonomyIds: asNumberArray(filters.department_taxonomy_ids),
    educationUg: asEducationLevel(filters.education_ug),
    educationPg: asEducationLevel(filters.education_pg),
    educationDoctorate: asEducationLevel(filters.education_doctorate),
    gender: (filters.gender as Draft['gender']) || '',
    hasCareerBreak: asBool(filters.has_career_break),
    isDifferentlyAbled: asBool(filters.is_differently_abled),
    defenceBackground: (filters.defence_background as Draft['defenceBackground']) || '',
    candidateCategory: (filters.candidate_category as Draft['candidateCategory']) || '',
    ageMin: asNumber(filters.age_min) != null ? String(filters.age_min) : '',
    ageMax: asNumber(filters.age_max) != null ? String(filters.age_max) : '',
    workPermitCountries: asStringArray(filters.work_permit_countries),
    sort: 'relevance',
  }
}

function buildActiveFilterChips(filters: CandidateSearchFilters): { label: string; value: string }[] {
  const chips: { label: string; value: string }[] = []

  if (filters.keywords?.length) {
    chips.push({ label: 'Keywords', value: filters.keywords.map((k) => k.value).join(', ') })
  }
  if (filters.skill_filters?.length) {
    chips.push({
      label: 'Skills',
      value: filters.skill_filters.map((s) => (s.min_years ? `${s.name} (${s.min_years}+ yrs)` : s.name)).join(', '),
    })
  }
  if (filters.current_locations?.length) {
    chips.push({ label: 'Location', value: filters.current_locations.join(', ') })
  }
  if (filters.experience_min_months != null || filters.experience_max_months != null) {
    const min = filters.experience_min_months != null ? Math.round(filters.experience_min_months / 12) : 0
    const max = filters.experience_max_months != null ? Math.round(filters.experience_max_months / 12) : '∞'
    chips.push({ label: 'Experience', value: `${min}-${max} yrs` })
  }
  if (filters.notice_period_bucket) {
    chips.push({
      label: 'Notice period',
      value: NOTICE_PERIOD_OPTIONS.find((o) => o.value === filters.notice_period_bucket)?.label ?? filters.notice_period_bucket,
    })
  }
  if (filters.current_company) chips.push({ label: 'Current company', value: filters.current_company })
  if (filters.current_designation) chips.push({ label: 'Current designation', value: filters.current_designation })
  if (filters.gender) chips.push({ label: 'Gender', value: filters.gender })
  if (filters.candidate_category) chips.push({ label: 'Category', value: filters.candidate_category })

  return chips
}

interface MatchLine {
  label: string
  matched: boolean
  detail: string
}

/**
 * Per-candidate "what matched" summary shown on result-card hover — a
 * plain client-side comparison of this candidate's own fields (already in
 * the API response) against the currently active search filters. No
 * backend call, no AI: it's a compact echo of data both sides already have.
 */
function buildMatchSummary(
  filters: CandidateSearchFilters,
  result: KeywordMatchedCandidate,
): { matchedSkills: string[]; lines: MatchLine[] } {
  const matchedSkills: string[] = []
  const lines: MatchLine[] = []

  const wantedTerms = [
    ...(filters.skill_filters ?? []).map((s) => s.name),
    ...(filters.keywords ?? []).map((k) => k.value),
  ].map((s) => s.toLowerCase())

  if (wantedTerms.length) {
    const matched = result.topSkills.filter((skill) => wantedTerms.includes(skill.toLowerCase()))
    matchedSkills.push(...matched)
    lines.push({
      label: 'Keywords / Skills',
      matched: matched.length > 0,
      detail: matched.length ? matched.join(', ') : 'No direct skill match',
    })
  }

  if (filters.current_locations?.length) {
    const candidateLocations = [result.currentCity, result.currentState].filter((v): v is string => !!v)
    const matched = filters.current_locations.some((loc) =>
      candidateLocations.some((c) => c.toLowerCase() === loc.toLowerCase()),
    )
    lines.push({ label: 'Location', matched, detail: candidateLocations.join(', ') || '—' })
  }

  if (filters.experience_min_months != null || filters.experience_max_months != null) {
    const months = result.totalExperienceMonths ?? 0
    const min = filters.experience_min_months ?? 0
    const max = filters.experience_max_months ?? Number.POSITIVE_INFINITY
    lines.push({ label: 'Experience', matched: months >= min && months <= max, detail: `${Math.round(months / 12)} yrs` })
  }

  if (filters.current_company) {
    lines.push({
      label: 'Current company',
      matched: (result.currentCompany ?? '').toLowerCase() === filters.current_company.toLowerCase(),
      detail: result.currentCompany ?? '—',
    })
  }

  if (filters.current_designation) {
    lines.push({
      label: 'Current designation',
      matched: (result.currentDesignation ?? '').toLowerCase() === filters.current_designation.toLowerCase(),
      detail: result.currentDesignation ?? '—',
    })
  }

  return { matchedSkills, lines }
}

/**
 * Candidate Search v2 — two distinct states, matching Naukri Resdex's
 * actual flow: a full-width Search Builder page (this app's existing
 * filter form, unchanged field-for-field), and — only after "Search
 * candidates" is clicked — a separate Results state with a compact left
 * filter sidebar + right candidate list. These are deliberately NOT
 * merged into one always-visible layout; "Modify" on the results state
 * returns to the builder with every field still populated (`draft` state
 * is shared across both states and never reset by the transition).
 * Deliberately not AI: matchScore is pure OpenSearch keyword relevance —
 * see CandidateSearchV2Controller.
 */
export function CandidateSearchV2Page() {
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [filters, setFilters] = useState<CandidateSearchFilters>({ sort: 'relevance', page: 1, per_page: DEFAULT_PAGE_SIZE })
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)
  const [saveSearchName, setSaveSearchName] = useState('')
  const [showSaveInput, setShowSaveInput] = useState(false)
  const [showFiltersPopover, setShowFiltersPopover] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [reminderTarget, setReminderTarget] = useState<{ id: number; name: string } | null>(null)

  const { data, isPending, error: searchError } = useCandidateSearchV2Query(filters, hasSearched)
  const { data: filterOptions } = useCandidateFilterOptions()
  const { data: recentSearches } = useRecentCandidateSearches()
  const { data: savedSearches } = useCandidateSavedSearches()
  const saveSearch = useSaveCandidateSearch()
  const deleteSavedSearch = useDeleteCandidateSavedSearch()

  const patchDraft = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }))

  const runSearch = () => {
    setFilters({ ...draftToFilters(draft), per_page: filters.per_page ?? DEFAULT_PAGE_SIZE })
    setHasSearched(true)
    setSelectedIds(new Set())
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const resetDraft = () => setDraft(EMPTY_DRAFT)

  // "Modify" — same draft state, just switch back to the builder view.
  const modifySearch = () => setHasSearched(false)

  const fillSearch = (rawFilters: Record<string, unknown>) => {
    setDraft(filtersToDraft(rawFilters))
  }

  const searchProfiles = (rawFilters: Record<string, unknown>) => {
    const filled = filtersToDraft(rawFilters)
    setDraft(filled)
    setFilters({ ...draftToFilters(filled), per_page: filters.per_page ?? DEFAULT_PAGE_SIZE })
    setHasSearched(true)
    setSelectedIds(new Set())
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSaveSearch = async () => {
    if (!saveSearchName.trim()) return
    await saveSearch.mutateAsync({ name: saveSearchName.trim(), filters: draftToFilters(draft) })
    setSaveSearchName('')
    setShowSaveInput(false)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (!data) return
    const allIds = data.results.map((r) => r.studentProfileId)
    const allSelected = allIds.every((id) => selectedIds.has(id))
    setSelectedIds(allSelected ? new Set() : new Set(allIds))
  }

  const activeFilterChips = buildActiveFilterChips(filters)
  const draftFilterCount = buildActiveFilterChips(draftToFilters(draft)).length
  const allOnPageSelected = !!data && data.results.length > 0 && data.results.every((r) => selectedIds.has(r.studentProfileId))

  // The exact same filter fields, unchanged — reused as-is in both the
  // full-width Search Builder state and the compact Results-page sidebar,
  // so there is only ever one implementation of each field to keep in sync.
  const filterFieldsAccordion = (
    <div className="accordion">
      <AccordionSection title="Keywords" defaultOpen badge={draft.keywords.length ? String(draft.keywords.length) : undefined}>
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-keyword-scope">
            Search in
          </label>
          <select
            id="v2-keyword-scope"
            className="form-select form-select-sm"
            value={draft.keywordScope}
            onChange={(e) => patchDraft({ keywordScope: e.target.value as KeywordScope })}
          >
            <option value="entire_resume">Entire Resume</option>
            <option value="title">Resume Title / Headline</option>
            <option value="summary">Summary</option>
            <option value="skills">Key Skills</option>
          </select>
        </div>
        <KeywordChipInput
          chips={draft.keywords}
          onChange={(keywords) => patchDraft({ keywords })}
          suggestions={filterOptions?.skills ?? []}
        />
      </AccordionSection>

      <AccordionSection title="Skills" badge={draft.skillFilters.length ? String(draft.skillFilters.length) : undefined}>
        <SkillExperienceInput
          chips={draft.skillFilters}
          onChange={(skillFilters) => patchDraft({ skillFilters })}
          suggestions={filterOptions?.skills ?? []}
        />
      </AccordionSection>

      <AccordionSection title="Experience & Notice Period">
        <div className="mb-3">
          <label className="form-label fw-medium" htmlFor="v2-exp-min">
            Experience (years)
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              id="v2-exp-min"
              type="number"
              min={0}
              className="form-control"
              placeholder="Min"
              value={draft.experience_min_years}
              onChange={(e) => patchDraft({ experience_min_years: e.target.value })}
            />
            <span className="text-muted">to</span>
            <input
              type="number"
              min={0}
              className="form-control"
              placeholder="Max"
              aria-label="Maximum experience in years"
              value={draft.experience_max_years}
              onChange={(e) => patchDraft({ experience_max_years: e.target.value })}
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label fw-medium" htmlFor="v2-notice-bucket">
            Notice period / Availability
          </label>
          <select
            id="v2-notice-bucket"
            className="form-select"
            value={draft.noticePeriodBucket}
            onChange={(e) => patchDraft({ noticePeriodBucket: e.target.value as NoticePeriodBucket | '' })}
          >
            {NOTICE_PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label fw-medium" htmlFor="v2-serving-notice">
            Currently serving notice period?
          </label>
          <select
            id="v2-serving-notice"
            className="form-select"
            value={draft.isServingNoticePeriod}
            onChange={(e) => patchDraft({ isServingNoticePeriod: e.target.value as TriState })}
          >
            <option value="">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      </AccordionSection>

      <AccordionSection title="Location">
        <div className="mb-2">
          <span className="form-label fw-medium d-block">Current location of candidate</span>
          <MultiSuggestInput
            values={draft.locations}
            onChange={(locations) => patchDraft({ locations })}
            suggestions={filterOptions?.locations ?? []}
            suggestionsLabel="Suggested locations"
            placeholder="Add location"
          />
        </div>
        <div className="form-check mb-1">
          <input
            type="checkbox"
            className="form-check-input"
            id="willing-to-relocate"
            checked={draft.willingToRelocate === 'yes'}
            onChange={(e) => patchDraft({ willingToRelocate: e.target.checked ? 'yes' : '' })}
          />
          <label className="form-check-label fs-13" htmlFor="willing-to-relocate">
            Willing to relocate
          </label>
        </div>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="exclude-anywhere"
            checked={draft.excludeAnywhere}
            onChange={(e) => patchDraft({ excludeAnywhere: e.target.checked })}
          />
          <label className="form-check-label fs-13" htmlFor="exclude-anywhere">
            Exclude candidates open to "Anywhere"
          </label>
        </div>
      </AccordionSection>

      <AccordionSection title="Salary">
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-salary-currency">
            Currency
          </label>
          <input
            id="v2-salary-currency"
            type="text"
            className="form-control"
            placeholder="INR"
            maxLength={3}
            value={draft.salaryCurrency}
            onChange={(e) => patchDraft({ salaryCurrency: e.target.value.toUpperCase() })}
          />
        </div>
        <div className="form-check mb-3">
          <input
            type="checkbox"
            className="form-check-input"
            id="include-undisclosed"
            checked={draft.includeUndisclosedSalary}
            onChange={(e) => patchDraft({ includeUndisclosedSalary: e.target.checked })}
          />
          <label className="form-check-label fs-13" htmlFor="include-undisclosed">
            Include candidates who haven't disclosed salary
          </label>
        </div>
        <div className="mb-3">
          <label className="form-label fw-medium" htmlFor="v2-salary-min">
            Expected annual salary
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              id="v2-salary-min"
              type="number"
              min={0}
              className="form-control"
              placeholder="Min"
              value={draft.salary_min}
              onChange={(e) => patchDraft({ salary_min: e.target.value })}
            />
            <span className="text-muted">to</span>
            <input
              type="number"
              min={0}
              className="form-control"
              placeholder="Max"
              aria-label="Maximum expected salary"
              value={draft.salary_max}
              onChange={(e) => patchDraft({ salary_max: e.target.value })}
            />
          </div>
        </div>
        <div>
          <label className="form-label fw-medium" htmlFor="v2-current-salary-min">
            Current annual salary
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              id="v2-current-salary-min"
              type="number"
              min={0}
              className="form-control"
              placeholder="Min"
              value={draft.currentSalaryMin}
              onChange={(e) => patchDraft({ currentSalaryMin: e.target.value })}
            />
            <span className="text-muted">to</span>
            <input
              type="number"
              min={0}
              className="form-control"
              placeholder="Max"
              aria-label="Maximum current salary"
              value={draft.currentSalaryMax}
              onChange={(e) => patchDraft({ currentSalaryMax: e.target.value })}
            />
          </div>
        </div>
      </AccordionSection>

      <AccordionSection title="Education Details">
        <EducationLevelFilterFields
          label="Under Graduate (UG)"
          value={draft.educationUg}
          onChange={(educationUg) => patchDraft({ educationUg })}
        />
        <EducationLevelFilterFields
          label="Post Graduate (PG)"
          value={draft.educationPg}
          onChange={(educationPg) => patchDraft({ educationPg })}
        />
        <EducationLevelFilterFields
          label="Doctorate / PPG"
          value={draft.educationDoctorate}
          onChange={(educationDoctorate) => patchDraft({ educationDoctorate })}
        />
      </AccordionSection>

      <AccordionSection title="Diversity Hiring">
        <p className="fs-12 text-muted">
          These filters only match candidates who have chosen to share this information — they never penalize
          candidates who haven't.
        </p>
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-gender">
            Gender
          </label>
          <select
            id="v2-gender"
            className="form-select"
            value={draft.gender}
            onChange={(e) => patchDraft({ gender: e.target.value as Draft['gender'] })}
          >
            <option value="">All</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-career-break">
            Career break
          </label>
          <select
            id="v2-career-break"
            className="form-select"
            value={draft.hasCareerBreak}
            onChange={(e) => patchDraft({ hasCareerBreak: e.target.value as TriState })}
          >
            <option value="">Any</option>
            <option value="yes">Has career break</option>
            <option value="no">No career break</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label fw-medium" htmlFor="v2-differently-abled">
            Differently-abled
          </label>
          <select
            id="v2-differently-abled"
            className="form-select"
            value={draft.isDifferentlyAbled}
            onChange={(e) => patchDraft({ isDifferentlyAbled: e.target.value as TriState })}
          >
            <option value="">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
        <div>
          <label className="form-label fw-medium" htmlFor="v2-defence-background">
            Defence background
          </label>
          <select
            id="v2-defence-background"
            className="form-select"
            value={draft.defenceBackground}
            onChange={(e) => patchDraft({ defenceBackground: e.target.value as Draft['defenceBackground'] })}
          >
            <option value="">Any</option>
            <option value="army">Army</option>
            <option value="navy">Navy</option>
            <option value="air_force">Air Force</option>
            <option value="other_paramilitary">Other Paramilitary</option>
          </select>
        </div>
      </AccordionSection>

      <AccordionSection title="Department, Role & Industry">
        <div className="mb-3">
          <label className="form-label fw-medium" htmlFor="v2-department-taxonomy">
            Department / Role
          </label>
          <select
            id="v2-department-taxonomy"
            multiple
            className="form-select"
            style={{ minHeight: 120 }}
            value={draft.departmentTaxonomyIds.map(String)}
            onChange={(e) =>
              patchDraft({
                departmentTaxonomyIds: Array.from(e.target.selectedOptions).map((o) => Number(o.value)),
              })
            }
          >
            {(filterOptions?.departmentTaxonomies ?? []).map((node) => (
              <option key={node.id} value={node.id}>
                {node.breadcrumb}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label fw-medium" htmlFor="v2-industry">
            Industry
          </label>
          <select
            id="v2-industry"
            multiple
            className="form-select"
            style={{ minHeight: 120 }}
            value={draft.industryIds.map(String)}
            onChange={(e) => patchDraft({ industryIds: Array.from(e.target.selectedOptions).map((o) => Number(o.value)) })}
          >
            {(filterOptions?.industries ?? []).map((industry) => (
              <option key={industry.id} value={industry.id}>
                {industry.name}
              </option>
            ))}
          </select>
        </div>
      </AccordionSection>

      <AccordionSection title="Company & Designation">
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-current-company">
            Current company
          </label>
          <input
            id="v2-current-company"
            type="text"
            className="form-control"
            list="company-options"
            value={draft.currentCompany}
            onChange={(e) => patchDraft({ currentCompany: e.target.value })}
          />
        </div>
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-previous-company">
            Previous company
          </label>
          <input
            id="v2-previous-company"
            type="text"
            className="form-control"
            list="company-options"
            value={draft.previousCompany}
            onChange={(e) => patchDraft({ previousCompany: e.target.value })}
          />
        </div>
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-current-designation">
            Current designation
          </label>
          <input
            id="v2-current-designation"
            type="text"
            className="form-control"
            list="designation-options"
            value={draft.currentDesignation}
            onChange={(e) => patchDraft({ currentDesignation: e.target.value })}
          />
        </div>
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-previous-designation">
            Previous designation
          </label>
          <input
            id="v2-previous-designation"
            type="text"
            className="form-control"
            list="designation-options"
            value={draft.previousDesignation}
            onChange={(e) => patchDraft({ previousDesignation: e.target.value })}
          />
        </div>
        <datalist id="company-options">
          {(filterOptions?.companies ?? []).map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <datalist id="designation-options">
          {(filterOptions?.designations ?? []).map((d) => (
            <option key={d} value={d} />
          ))}
        </datalist>
        <div className="mb-2">
          <span className="form-label fw-medium d-block">Exclude companies</span>
          <MultiSuggestInput
            values={draft.excludeCompanies}
            onChange={(excludeCompanies) => patchDraft({ excludeCompanies })}
            suggestions={filterOptions?.companies ?? []}
            placeholder="Add company to exclude"
          />
        </div>
        <div>
          <span className="form-label fw-medium d-block">Exclude designations</span>
          <MultiSuggestInput
            values={draft.excludeDesignations}
            onChange={(excludeDesignations) => patchDraft({ excludeDesignations })}
            suggestions={filterOptions?.designations ?? []}
            placeholder="Add designation to exclude"
          />
        </div>
      </AccordionSection>

      <AccordionSection title="Additional Details">
        <div className="mb-2">
          <label className="form-label fw-medium" htmlFor="v2-candidate-category">
            Candidate category
          </label>
          <select
            id="v2-candidate-category"
            className="form-select"
            value={draft.candidateCategory}
            onChange={(e) => patchDraft({ candidateCategory: e.target.value as Draft['candidateCategory'] })}
          >
            <option value="">Any</option>
            <option value="fresher">Fresher</option>
            <option value="experienced">Experienced</option>
            <option value="intern">Intern</option>
            <option value="freelancer">Freelancer</option>
            <option value="returning_professional">Returning Professional</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label fw-medium" htmlFor="v2-age-min">
            Age (years)
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              id="v2-age-min"
              type="number"
              min={14}
              className="form-control"
              placeholder="Min"
              value={draft.ageMin}
              onChange={(e) => patchDraft({ ageMin: e.target.value })}
            />
            <span className="text-muted">to</span>
            <input
              type="number"
              min={14}
              className="form-control"
              placeholder="Max"
              aria-label="Maximum age"
              value={draft.ageMax}
              onChange={(e) => patchDraft({ ageMax: e.target.value })}
            />
          </div>
        </div>
        <div>
          <span className="form-label fw-medium d-block">Work permit / authorized countries</span>
          <MultiSuggestInput
            values={draft.workPermitCountries}
            onChange={(workPermitCountries) => patchDraft({ workPermitCountries })}
            placeholder="Add 2-letter country code, e.g. US"
          />
        </div>
      </AccordionSection>

      <AccordionSection title="Sort">
        <select
          className="form-select"
          value={draft.sort}
          onChange={(e) => patchDraft({ sort: e.target.value as CandidateSearchFilters['sort'] })}
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </AccordionSection>
    </div>
  )

  return (
    <>
      {/* Gradient banner */}
      <div
        className="card custom-card mb-4 mt-4"
        style={{ background: 'linear-gradient(135deg, var(--primary-color) 0%, #7c6ef5 100%)', border: 'none' }}
      >
        <div className="card-body py-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h5 className="fw-semibold mb-1 text-white">Candidate Search</h5>
              <p className="mb-0 fs-13" style={{ color: 'rgba(255,255,255,0.8)' }}>
                Search and discover candidates from your mapped program pool.
              </p>
            </div>
            {hasSearched && (
              <button type="button" className="btn btn-light btn-sm fw-semibold" onClick={modifySearch}>
                <i className="ti ti-adjustments-horizontal me-1" />Modify Search
              </button>
            )}
          </div>
        </div>
      </div>

      {!hasSearched ? (
        // --- STATE 1: Search Builder — the existing filter form, full width, no results. ---
        <div className="row g-3">
          <div className="col-lg-8">
            <div className="card custom-card">
              <div
                className="card-header"
                style={{ background: 'linear-gradient(90deg, rgba(var(--primary-rgb),0.08) 0%, transparent 100%)' }}
              >
                <div className="card-title fw-semibold fs-14 d-flex align-items-center gap-2">
                  <span className="avatar avatar-sm avatar-rounded bg-primary">
                    <i className="ti ti-filter fs-14 text-white" />
                  </span>
                  Search Filters
                </div>
                {draftFilterCount > 0 && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary-transparent text-primary fs-11">{draftFilterCount} filters</span>
                    <button type="button" className="btn btn-link btn-sm p-0 fs-12 text-danger" onClick={resetDraft}>
                      Reset
                    </button>
                  </div>
                )}
              </div>
              <div className="card-body p-0">
                {filterFieldsAccordion}
              </div>
              <div className="card-footer d-flex justify-content-end gap-2">
                {draftFilterCount > 0 && (
                  <button type="button" className="btn btn-light btn-sm" onClick={resetDraft}>
                    <i className="ti ti-x me-1" />Clear All
                  </button>
                )}
                <Button onClick={runSearch}>
                  <i className="ti ti-search me-1" />Search Candidates
                </Button>
              </div>
            </div>
          </div>

          <div className="col-lg-4 d-flex flex-column gap-3">
            <div className="card custom-card">
              <div className="card-header" style={{ background: 'linear-gradient(90deg, rgba(var(--primary-rgb),0.08) 0%, transparent 100%)' }}>
                <div className="card-title fw-semibold fs-14 d-flex align-items-center gap-2">
                  <i className="ti ti-bookmark text-primary" />Save this search
                </div>
              </div>
              <div className="card-body">
                <div className="input-group input-group-sm">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search name"
                    value={saveSearchName}
                    onChange={(e) => setSaveSearchName(e.target.value)}
                  />
                  <Button onClick={handleSaveSearch} loading={saveSearch.isPending} disabled={!saveSearchName.trim()}>
                    Save
                  </Button>
                </div>
              </div>
            </div>

            <div className="card custom-card">
              <div className="card-header" style={{ background: 'linear-gradient(90deg, rgba(var(--primary-rgb),0.08) 0%, transparent 100%)' }}>
                <div className="card-title fw-semibold fs-14 d-flex align-items-center gap-2">
                  <i className="ti ti-bookmarks text-primary" />Saved Searches
                </div>
              </div>
              <div className="card-body">
                {!savedSearches || savedSearches.length === 0 ? (
                  <p className="text-muted fs-13 mb-0">You haven't saved any searches yet.</p>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {savedSearches.map((search) => (
                      <div key={search.id} className="pb-3 border-bottom">
                        <p className="fs-13 fw-medium mb-1">{search.name}</p>
                        <p className="fs-11 text-muted mb-2">Saved {formatRelativeTime(search.createdAt)}</p>
                        <div className="d-flex gap-3">
                          <button type="button" className="btn btn-link btn-sm p-0 fs-12" onClick={() => fillSearch(search.filters)}>
                            Fill this search
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 fs-12"
                            onClick={() => searchProfiles(search.filters)}
                          >
                            Search profiles
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 fs-12 text-danger"
                            onClick={() => deleteSavedSearch.mutate(search.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="card custom-card">
              <div className="card-header" style={{ background: 'linear-gradient(90deg, rgba(var(--primary-rgb),0.08) 0%, transparent 100%)' }}>
                <div className="card-title fw-semibold fs-14 d-flex align-items-center gap-2">
                  <i className="ti ti-history text-primary" />Recent Searches
                </div>
              </div>
              <div className="card-body">
                {!recentSearches || recentSearches.length === 0 ? (
                  <p className="text-muted fs-13 mb-0">There is no recent search right now.</p>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {recentSearches.map((search) => (
                      <div key={search.id} className="pb-3 border-bottom">
                        <p className="fs-13 mb-1">{search.summary}</p>
                        <p className="fs-11 text-muted mb-2">
                          {search.resultsCount} result{search.resultsCount === 1 ? '' : 's'} · {formatRelativeTime(search.searchedAt)}
                        </p>
                        <div className="d-flex gap-3">
                          <button type="button" className="btn btn-link btn-sm p-0 fs-12" onClick={() => fillSearch(search.filters)}>
                            Fill this search
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 fs-12"
                            onClick={() => searchProfiles(search.filters)}
                          >
                            Search profiles
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // --- STATE 2: Search Results — a separate view: summary bar, left filter sidebar, right results. ---
        <>
          <div className="card custom-card mb-3" style={{ borderLeft: '4px solid var(--primary-color)' }}>
            <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="avatar avatar-sm avatar-rounded bg-primary-transparent">
                  <i className="ti ti-users fs-14 text-primary" />
                </span>
                {activeFilterChips.length === 0 ? (
                  <span className="fs-14 fw-semibold">All candidates</span>
                ) : (
                  <span className="fs-14">
                    <strong className="text-primary">{isPending ? '…' : data?.meta.total ?? 0}</strong>
                    {' '}profiles found for{' '}
                    <strong>{activeFilterChips.map((c) => c.value).join(', ')}</strong>
                  </span>
                )}
              </div>
              <div className="position-relative">
                <Button variant="secondary" onClick={() => setShowSaveInput((v) => !v)}>
                  Save Search
                </Button>
                {showSaveInput && (
                  <div className="position-absolute top-100 end-0 mt-2 card shadow" style={{ zIndex: 30, minWidth: 260 }}>
                    <div className="card-body">
                      <label className="form-label fs-13" htmlFor="v2-save-search-name">
                        Name this search
                      </label>
                      <div className="input-group">
                        <input
                          id="v2-save-search-name"
                          type="text"
                          className="form-control"
                          placeholder="Search name"
                          value={saveSearchName}
                          onChange={(e) => setSaveSearchName(e.target.value)}
                        />
                        <Button onClick={handleSaveSearch} loading={saveSearch.isPending} disabled={!saveSearchName.trim()}>
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-3">
              <div
                style={{ position: 'sticky', top: 84, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}
              >
                <div className="card custom-card">
                  <div
                    className="card-header"
                    style={{ background: 'linear-gradient(90deg, rgba(var(--primary-rgb),0.08) 0%, transparent 100%)' }}
                  >
                    <div className="card-title fw-semibold fs-14 d-flex align-items-center gap-2">
                      <span className="avatar avatar-sm avatar-rounded bg-primary">
                        <i className="ti ti-filter fs-13 text-white" />
                      </span>
                      Filters
                      {draftFilterCount > 0 && (
                        <span className="badge bg-primary-transparent text-primary fs-11">{draftFilterCount}</span>
                      )}
                    </div>
                    {draftFilterCount > 0 && (
                      <button type="button" className="btn btn-link btn-sm p-0 fs-12 text-danger" onClick={resetDraft}>
                        Reset
                      </button>
                    )}
                  </div>
                  <div className="card-body p-0">
                    {filterFieldsAccordion}
                  </div>
                  <div className="card-footer d-grid">
                    <Button onClick={runSearch}>
                      <i className="ti ti-search me-1" />Search Candidates
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-9">
              <div className="card custom-card mb-3">
                <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="position-relative">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 fw-medium text-start"
                      onClick={() => setShowFiltersPopover((v) => !v)}
                      disabled={activeFilterChips.length === 0}
                    >
                      {isPending ? 'Searching…' : `${data?.meta.total ?? 0} profiles found`}
                      {activeFilterChips.length > 0 && ' · View filters'}
                    </button>
                    {showFiltersPopover && activeFilterChips.length > 0 && (
                      <div
                        className="position-absolute top-100 start-0 mt-2 card shadow"
                        style={{ zIndex: 30, minWidth: 320, maxWidth: 420 }}
                      >
                        <div className="card-body">
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <p className="fw-semibold fs-13 mb-0">Showing results for</p>
                            <button
                              type="button"
                              className="btn-close fs-11"
                              aria-label="Close"
                              onClick={() => setShowFiltersPopover(false)}
                            />
                          </div>
                          <div className="d-flex flex-column gap-2">
                            {activeFilterChips.map((chip) => (
                              <div key={chip.label} className="fs-12">
                                <span className="text-muted">{chip.label}: </span>
                                {chip.value}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="d-flex align-items-center gap-2">
                      <label htmlFor="v2-sort-select" className="fs-13 text-muted mb-0">
                        Sort by
                      </label>
                      <select
                        id="v2-sort-select"
                        className="form-select form-select-sm"
                        value={filters.sort}
                        onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value as CandidateSearchFilters['sort'], page: 1 }))}
                      >
                        {SORT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <label htmlFor="v2-page-size" className="fs-13 text-muted mb-0">
                        Show
                      </label>
                      <select
                        id="v2-page-size"
                        className="form-select form-select-sm"
                        value={filters.per_page ?? DEFAULT_PAGE_SIZE}
                        onChange={(e) => setFilters((f) => ({ ...f, per_page: Number(e.target.value), page: 1 }))}
                      >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {!!data && data.results.length > 0 && (
                  <div className="card-body border-top py-2">
                    <div className="form-check mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="select-all-results"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAll}
                      />
                      <label className="form-check-label fs-13" htmlFor="select-all-results">
                        Select all{selectedIds.size > 0 ? ` (${selectedIds.size} selected)` : ''}
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {isPending ? (
                <p className="text-muted">Searching…</p>
              ) : searchError ? (
                <div className="alert alert-danger fs-13" role="alert">
                  <i className="ti ti-alert-triangle me-1" />
                  Candidate search failed: {searchError.message || 'Unexpected error'}. Please try again.
                </div>
              ) : !data || data.results.length === 0 ? (
                <EmptyState heading="No candidates found." message="Try adjusting your filters." />
              ) : (
                <>
                  <div className="row g-3">
                    {data.results.map((result) => {
                      const { matchedSkills, lines } = buildMatchSummary(filters, result)

                      return (
                        <div className="col-12" key={result.studentProfileId}>
                          <ProfileCard
                            name={result.name}
                            program={result.program}
                            headline={result.headline}
                            photoUrl={result.photoUrl}
                            currentCity={result.currentCity}
                            currentState={result.currentState}
                            currentCompany={result.currentCompany}
                            currentDesignation={result.currentDesignation}
                            totalExperienceMonths={result.totalExperienceMonths}
                            isFresher={result.isFresher}
                            expectedCtc={result.expectedCtc}
                            noticePeriodDays={result.noticePeriodDays}
                            topSkills={result.topSkills}
                            matchedSkills={matchedSkills}
                            topEducations={result.topEducations}
                            preferredLocations={result.preferredLocations}
                            profileCompletionPercent={SHOW_PROFILE_COMPLETION ? result.profileCompletionPercent : undefined}
                            hasResume={result.hasResume}
                            updatedAt={result.updatedAt}
                            selected={selectedIds.has(result.studentProfileId)}
                            onToggleSelect={() => toggleSelect(result.studentProfileId)}
                            onViewDetail={() => setSelectedProfileId(result.studentProfileId)}
                            onScheduleReminder={() => setReminderTarget({ id: result.studentProfileId, name: result.name })}
                            extra={<MatchScoreBadge score={result.matchScore} />}
                            hoverContent={
                              lines.length > 0 && (
                                <div>
                                  <p className="fw-semibold mb-1">Matched against your search</p>
                                  {lines.map((line) => (
                                    <p key={line.label} className="mb-0">
                                      {line.matched ? '✓' : '✗'} {line.label}: {line.detail}
                                    </p>
                                  ))}
                                </div>
                              )
                            }
                          />
                        </div>
                      )
                    })}
                  </div>
                  <div className="mt-3">
                    <Pagination meta={data.meta} onPageChange={(page) => setFilters((f) => ({ ...f, page }))} />
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <CandidateDetailDrawer
        show={selectedProfileId != null}
        onClose={() => setSelectedProfileId(null)}
        studentProfileId={selectedProfileId ?? undefined}
      />

      <ReminderTaskModal
        show={reminderTarget != null}
        onClose={() => setReminderTarget(null)}
        studentProfileId={reminderTarget?.id ?? null}
        candidateName={reminderTarget?.name}
      />
    </>
  )
}
